import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../ui/button'
import { ExternalLink, LifeBuoy, X, HelpCircle } from 'lucide-react'

// Bump this key when we change the banner content and want it to re-show for
// users who previously dismissed an older version.
const STORAGE_KEY = 'ems.welcomeBanner.dismissed.v1'

const HELP_DOC_URL =
    'https://www.dropbox.com/scl/fi/82tbc8nnmn8tu6xuifin3/NEW-AuthentiGATE-EMS-How-to.pdf?rlkey=ls8zozomowwd88w8179t61s1l&st=zbgl8wi4&dl=0'

/**
 * Banner shown at the top of every authenticated page promoting the new EMS,
 * linking to the help PDF and the in-app support form. Dismissible per user
 * (persisted in localStorage); use {@link WelcomeBannerTrigger} elsewhere to
 * let the user bring it back.
 */
export const WelcomeBanner: React.FC = () => {
    const [dismissed, setDismissed] = useState<boolean>(true)

    useEffect(() => {
        try {
            setDismissed(localStorage.getItem(STORAGE_KEY) === 'true')
        } catch {
            setDismissed(false)
        }
        const onShow = () => setDismissed(false)
        window.addEventListener('ems:show-welcome-banner', onShow)
        return () => window.removeEventListener('ems:show-welcome-banner', onShow)
    }, [])

    if (dismissed) return null

    const handleDismiss = () => {
        try {
            localStorage.setItem(STORAGE_KEY, 'true')
        } catch {
            /* private mode / quota — fall through */
        }
        setDismissed(true)
    }

    return (
        <div className="border-b border-orange-200 bg-orange-50">
            <div className="container mx-auto flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 text-sm text-orange-900">
                <span className="font-semibold">Welcome to the new EMS!</span>
                <span className="text-orange-800">
                    Need help getting started?
                </span>
                <a
                    href={HELP_DOC_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-medium underline hover:text-orange-700"
                >
                    Read the how-to guide
                    <ExternalLink className="h-3 w-3" />
                </a>
                <span className="text-orange-800">Hit a snag?</span>
                <Link
                    to="/support"
                    className="inline-flex items-center gap-1 font-medium underline hover:text-orange-700"
                >
                    <LifeBuoy className="h-3.5 w-3.5" />
                    Report an issue
                </Link>
                <Button
                    variant="ghost"
                    size="icon"
                    className="ml-auto h-7 w-7 text-orange-900 hover:bg-orange-100"
                    onClick={handleDismiss}
                    aria-label="Dismiss welcome banner"
                    title="Dismiss"
                >
                    <X className="h-4 w-4" />
                </Button>
            </div>
        </div>
    )
}

/**
 * Small ? button suitable for placement in the header. Re-opens the welcome
 * banner by clearing the dismissed flag and broadcasting an event so the
 * banner component (if mounted) re-renders immediately.
 */
export const WelcomeBannerTrigger: React.FC<{ className?: string }> = ({
    className,
}) => {
    const handleClick = () => {
        try {
            localStorage.removeItem(STORAGE_KEY)
        } catch {
            /* ignore */
        }
        window.dispatchEvent(new Event('ems:show-welcome-banner'))
    }

    return (
        <Button
            variant="ghost"
            size="icon"
            className={className}
            onClick={handleClick}
            aria-label="Show welcome banner"
            title="Show welcome banner & help"
        >
            <HelpCircle className="h-5 w-5" />
        </Button>
    )
}
